import { useState } from 'react';
import { Button, Form, Modal } from 'antd';
import BundleForm from '../forms/BundleForm';
import Bundle from '../../models/bundle';
import formType from '../../types/form';
import { CountryPrice, DefaultPrice } from '../../models/price';
import { Country } from '../../models/country';
import { CreateAction } from '../../store/types/actions';
import { defaultNotify, errorNotify } from '../common/Notify';

const { useForm } = Form;

interface AddBundleProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    bundlesNamesList: string[];
    defaultPrice: DefaultPrice;
    countries: Country[];
    error: null | string;
    addBundle: CreateAction<Bundle, void>;
}

interface FormCountryPrice {
    country: string;
    price: string | number;
    price_19: string | number;
}

const AddBundle: React.FC<AddBundleProps> = props => {
    const { open, setOpen, bundlesNamesList, defaultPrice, countries, addBundle } = props;
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [form] = useForm<Bundle>();

    const getPricesByCountryFromForm = (input: FormCountryPrice[] | undefined): CountryPrice[] | undefined => {
        if (!input) {
            return undefined;
        }
        const typed: CountryPrice[] = input.map((countryPrice) => ({
            country: countryPrice.country,
            price: +countryPrice.price,
            price_19: +countryPrice.price_19,
        }));

        // Check if any country is empty
        if (typed.some(countryPrice => countryPrice.country === '')) {
            return undefined;
        }

        const countryPricesMap:
            { [key: string]: { price: number, price_19: number } } = typed.reduce((prev, current) => {
                const { country, price, price_19: price19 } = current;
                return { ...prev, [country]: { price, price19 } };
            }, {});
        return Object
            .entries(countryPricesMap).map(([country, { price, price_19 }]) => ({ country, price, price_19 }));
    };

    const handleSubmit = async () => {
        const values = form.getFieldsValue();
        const {
            name,
            url,
            gp_url: gpURL,
            price,
            price_19: price19,
            price_by_country: priceByCountry,
        } = values;
        const pricesByCountry = getPricesByCountryFromForm(priceByCountry);

        const newBundle: Bundle = {
            name,
            url,
            gp_url: gpURL,
            price: +price,
            price_19: +price19,
            price_by_country: pricesByCountry,
            // TODO pass email here
            updated_by: '',
        };
        if (newBundle.name === '') {
            errorNotify('Please input bundle name', '');
            return;
        }
        if (newBundle.url === '' && newBundle.gp_url === '') {
            errorNotify('Please input URL or GP URL', '');
            return;
        }
        if (newBundle.price <= 0) {
            errorNotify('Please input price', '');
            return;
        }
        if (newBundle.price_19 <= 0) {
            errorNotify('Please input price 19', '');
            return;
        }
        setConfirmLoading(true);
        try {
            await addBundle(newBundle);
            setOpen(false);
            defaultNotify(`${name} bundle added`, '');
            form.resetFields();
        } catch (e: any) {
            errorNotify('Error adding bundle', e.message);
        } finally {
            setConfirmLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setOpen(false);
    };

    return (
        <Modal
            title="Add Bundle"
            open={open}
            confirmLoading={confirmLoading}
            onCancel={handleCancel}
            getContainer={false}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button onClick={handleSubmit} key="submit" htmlType="submit" type="primary" loading={confirmLoading}>

                    Submit
                </Button>,
            ]}
        >
            <BundleForm
                form={form}
                defaultPrice={defaultPrice}
                bundlesNamesList={bundlesNamesList}
                countries={countries}
                type={formType.ADD_FORM}
            />
        </Modal>
    );
};

export default AddBundle;
